import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { format } from 'date-fns';
import { DialogTitle } from '@radix-ui/react-dialog';

import { UsersIcon } from '@/assets/icons';
import { Button, Tag } from '@/components';
import { DialogClose, DialogContent } from '@/components/atoms/Dialog/Dialog.tsx';
import { formatNumberWithComma } from '@/utils/numberFormatter';

export type SurveyQuestionData = {
  title: string;
  description: string;
  tags: string[];
  audienceSize: number;
  startDate: string | undefined;
  distributionMethods: string;
};

type SurveyPublishPopupProps = {
  data: SurveyQuestionData;
};

export const SurveyPublishPopup: FC<SurveyPublishPopupProps> = ({ data }) => {
  const { t } = useTranslation('components');

  return (
    <DialogContent showCloseButton className="w-[460px]">
      <div>
        <DialogTitle className="text-xl font-semibold">
          {t('surveyBuilderPopup.surveyPublishPopup.title')}
        </DialogTitle>
      </div>
      <div className="space-y-5">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium text-custom-warm-grey-500">
            {t('surveyBuilderPopup.surveyPublishPopup.surveyTitle')}
          </span>
          <span className="text-sm">{data.title}</span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium text-custom-warm-grey-500">
            {t('surveyBuilderPopup.surveyPublishPopup.surveyDescription')}
          </span>
          <span className="text-sm">{data.description}</span>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-sm text-custom-warm-grey-500">
            {t('surveyBuilderPopup.surveyPublishPopup.tags')}
          </span>
          <div className="flex gap-2">
            {data.tags.map(tag => (
              <Tag label={tag} key={tag} color="cyan" size="regular" />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm text-custom-warm-grey-500">
            {t('surveyBuilderPopup.surveyPublishPopup.audienceSize')}
          </span>
          <div className="flex gap-1.5 text-subtitle-secondary items-center">
            <UsersIcon width={16} height={16} />
            <span className="text-sm">{formatNumberWithComma(data.audienceSize)}</span>
          </div>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium text-custom-warm-grey-500">
            {t('surveyBuilderPopup.surveyPublishPopup.startDate')}
          </span>
          <span className="text-sm">
            {data.startDate ? format(new Date(data.startDate), 'MMM dd, yyyy') : '-'}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium text-custom-warm-grey-500">
            {t('surveyBuilderPopup.surveyPublishPopup.distributionMethods')}
          </span>
          <span className="text-sm">{data.distributionMethods}</span>
        </div>
      </div>
      <div className="flex gap-3 mt-1">
        <DialogClose className="w-full" asChild>
          <Button variant="secondary" type="button" size="lg" className="w-full">
            {t('surveyBuilderPopup.surveyPublishPopup.cancel')}
          </Button>
        </DialogClose>
        <DialogClose className="w-full" asChild>
          <Button variant="primary" size="lg" className="w-full">
            {t('surveyBuilderPopup.surveyPublishPopup.save')}
          </Button>
        </DialogClose>
      </div>
    </DialogContent>
  );
};
